import type { ExtensionConfig } from './extension-config'
import { loadExtensionScript } from './utils/loader'
import type { ShellExtension, ShellExtensionCustomElements } from './shell-extension'
import type { GotoRegisteredApplications, Registrant } from '../core/models'
import type { RegisterApplicationConfig } from 'single-spa'
import { registerApplication } from 'single-spa'
import type { CustomProps } from '../common'
import { getShellLogger } from '../common/logger'
import { supportsCustomElements } from './utils/type-checking'
import { getExtensionConfigsWithOverrides } from './utils/extension-config-overrides'
import { filterForStandaloneRoutes, getStandaloneExtensionConfigForCurrentLocation } from './utils/standalone-filter'
import { getUnguardedRoutes } from '../core/helpers/unguarded-routes'
import { filterExtensionsByPrerequisites } from './utils/prerequisite-filter'
import { getLocationPathname } from '../common/dom-helpers'

export interface ExtensionConfigProvider {
  readonly getExtensionConfigs: () => readonly ExtensionConfig[]
}
/**
 * Interface to implements for managing extensions life cycle for the shell
 */
export interface ExtensionsManager extends Registrant<RegisterApplicationConfig<CustomProps>> {
  /**
   * Initialize all extensions.  It initializes them and also complete the namespace registration step.
   * @returns Promise that is completed when all initialization process is completed
   */
  initializeExtensions(): Promise<void>
  /**
   * Add the extensions configuration to the shell object
   *
   * @param config Array of extension config
   * @returns the Shell instance
   */
  addExtensions(extensionConfigs: readonly ExtensionConfig[]): void
  /**
   * Removes an extension config from the array of configs
   * @param id identifies the extension config to be removed
   */
  removeExtensionConfig(id: string): void
  /**
   * Loads extensions from the extensionConfig Array
   * @returns extension
   */
  loadExtensions(): Promise<void>
  /**
   * Retrieves all extensions loaded
   * @returns Extensions list
   */
  getExtensions(): readonly ShellExtension[]

  /**
   * Return the list of unguarded routes defined in the ExtensionConfig
   */
  getUnguardedRoutes(): readonly string[]
  /**
   * Returns true if the current extension is in standalone mode
   */
  isStandaloneRoute(route: string): boolean
}

/**
 * Class which manages extensions life cycle for the shell
 */
class ExtensionsManagerImpl implements ExtensionsManager, ExtensionConfigProvider {
  private readonly extensionConfigs = new Array<ExtensionConfig>()
  private extensions = new Map<ExtensionConfig, ShellExtension>()
  private readonly registeredApplications: GotoRegisteredApplications = new Map<
    string,
    RegisterApplicationConfig<CustomProps>
  >()

  private initializeExtension(extension: ShellExtension, config: ExtensionConfig): Promise<void> {
    const completeInitializationPromise = new Promise<void>((resolve, reject) => {
      const rejectWithFailure = (reason: unknown, message: string) => {
        this.extensions.delete(config)
        getShellLogger().error(message, reason)
        reject(reason)
      }
      extension
        .initialize(config)
        .then(() =>
          extension.registerNamespaces().catch(reason => {
            rejectWithFailure(reason, `${extension.id} failed when registering namespace`)
          }),
        )
        .then(() => {
          if (supportsCustomElements(extension)) {
            try {
              ;(extension as ShellExtensionCustomElements).registerCustomElements()
            } catch (e) {
              rejectWithFailure(e, `${extension.id} failed when registering custom elements`)
            }
          }
        })
        .then(() => resolve())
        .catch(reason => {
          rejectWithFailure(reason, `${extension.id} failed to initialize`)
        })
    })

    return completeInitializationPromise
  }

  public getExtensionConfigs() {
    return this.extensionConfigs
  }

  public initializeExtensions(): Promise<void> {
    const initializationPromises: Promise<void>[] = []
    this.extensions.forEach((extension, config) =>
      initializationPromises.push(this.initializeExtension(extension, config)),
    )
    return new Promise((resolve, reject) => {
      Promise.allSettled(initializationPromises)
        .then(() => resolve())
        .catch(reason => reject(reason))
    })
  }

  public addExtensions(extensionConfigs: readonly ExtensionConfig[]): void {
    this.extensionConfigs.push(...extensionConfigs)
  }

  public removeExtensionConfig(id: string): void {
    const extensionIdx = this.extensionConfigs.findIndex(config => config.id === id)
    if (extensionIdx > -1) {
      this.extensionConfigs.splice(extensionIdx, 1)
    }
  }

  public async loadExtensions(): Promise<void> {
    const transformedExtensionConfigs = this.applyExtensionConfigsTransformers()
    await this.internalLoadExtensions(transformedExtensionConfigs)
  }

  public getExtensions(): readonly ShellExtension[] {
    return Array.from(this.extensions.values())
  }

  public getUnguardedRoutes(): readonly string[] {
    return getUnguardedRoutes(this.extensionConfigs)
  }

  registerApplication(name: string, application: RegisterApplicationConfig<CustomProps>) {
    try {
      registerApplication(application)
      this.registeredApplications.set(name, application)
    } catch (e) {
      getShellLogger().error(`Cannot register the ${name} extension`, e)
    }
  }

  getRegisteredApplications() {
    return this.registeredApplications
  }

  private async internalLoadExtensions(extensionConfigs: readonly ExtensionConfig[]): Promise<void> {
    const extensionPromises = extensionConfigs.map(extensionConfig =>
      loadExtensionScript(extensionConfig).then(extension => {
        if (extension) {
          this.extensions.set(extensionConfig, extension)
          if (extension.id !== extensionConfig.id) {
            getShellLogger().warn(
              `The ${extensionConfig.id} id from the extension config does not match the ${extension.id} id provided by the extension script`,
            )
          }
        }
      }),
    )

    await Promise.allSettled(extensionPromises)
  }

  /**
   * Central function for applying any filters and overrides to the Extension Configs before Loading them
   */
  private applyExtensionConfigsTransformers() {
    const filters = [filterForStandaloneRoutes, filterExtensionsByPrerequisites]
    const overrides = this.getExtensionConfigsOverrides()
    const transformers = [...filters, ...overrides]
    return transformers.reduce(
      (transformedExtensionConfigs, transform) => transform(transformedExtensionConfigs, getLocationPathname()),
      this.extensionConfigs,
    )
  }

  isStandaloneRoute(route: string) {
    return !!getStandaloneExtensionConfigForCurrentLocation(this.extensionConfigs, route)
  }

  private getExtensionConfigsOverrides() {
    return [getExtensionConfigsWithOverrides]
  }
}

const extensionsManagerInstance = new ExtensionsManagerImpl()

export const getExtensionsManager = (): ExtensionsManager => extensionsManagerInstance
